export const MAX_PRELOAD_ERROR_RECOVERY_ATTEMPTS_PER_BUILD = 2;
export const PRELOAD_ERROR_RECOVERY_ATTEMPT_STORAGE_KEY = 'infra-struktur:preload-error-recovery-attempt';

export type PreloadErrorRecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>;

interface PreloadErrorRecoveryAttempts {
	attempts: number;
	buildVersion: string;
}

interface PreloadErrorRecoveryEnvironment {
	clearCaches: () => Promise<void>;
	reloadPage: () => void;
	updateServiceWorker: () => Promise<void>;
}

const parseStoredAttempts = (value: null | string, buildVersion: string): PreloadErrorRecoveryAttempts => {
	const fresh: PreloadErrorRecoveryAttempts = { attempts: 0, buildVersion };

	if (value === null) {
		return fresh;
	}

	try {
		const parsed = JSON.parse(value) as Partial<PreloadErrorRecoveryAttempts> | null;

		if (
			parsed === null ||
			typeof parsed !== 'object' ||
			parsed.buildVersion !== buildVersion ||
			typeof parsed.attempts !== 'number' ||
			!Number.isFinite(parsed.attempts)
		) {
			return fresh;
		}

		return { attempts: parsed.attempts, buildVersion };
	} catch {
		return fresh;
	}
};

/**
 * Das Budget hängt an der Build-Version, nicht an der Uhr: Ein neuer Build bekommt frische
 * Versuche, innerhalb eines Builds bleibt eine Reload-Schleife auf MAX_… Durchläufe begrenzt.
 */
export const claimPreloadErrorRecoveryAttempt = (
	storage: PreloadErrorRecoveryStorage,
	buildVersion: string
): boolean => {
	const stored = parseStoredAttempts(storage.getItem(PRELOAD_ERROR_RECOVERY_ATTEMPT_STORAGE_KEY), buildVersion);

	if (stored.attempts >= MAX_PRELOAD_ERROR_RECOVERY_ATTEMPTS_PER_BUILD) {
		return false;
	}

	storage.setItem(
		PRELOAD_ERROR_RECOVERY_ATTEMPT_STORAGE_KEY,
		JSON.stringify({ attempts: stored.attempts + 1, buildVersion } satisfies PreloadErrorRecoveryAttempts)
	);
	return true;
};

/**
 * Der Service Worker bleibt registriert: Ohne ihn würde die nächste Navigation eine gecachte
 * index.html gegen einen Server ohne deren Chunks laden. Stattdessen wird sein Update angestoßen
 * und der Precache geleert, damit die Navigation nach dem Reload die aktuelle Shell holt.
 */
export const runPreloadErrorRecovery = async (environment: PreloadErrorRecoveryEnvironment): Promise<void> => {
	const results = await Promise.allSettled([environment.updateServiceWorker(), environment.clearCaches()]);

	results.forEach((result, index) => {
		if (result.status === 'rejected') {
			const step = index === 0 ? 'updating the service worker' : 'clearing caches';
			console.warn(`Preload error recovery: ${step} failed:`, result.reason);
		}
	});

	environment.reloadPage();
};
